import React, { useState, useEffect, useRef, useCallback } from 'react';
import SingleSelect from './SingleSelect';
import instance from '../../axios';

const LIMIT = 10;

export const SubTypeSelect = ({
  expenseTypeId,
  vehicleId,
  value,
  handleChange,
  selectName = 'expenseSubTypeId',
  index = 0,
  label,
  translate,
  readOnly,
  isDisabled,
  canRemove = true,
  canAssign = true,
  placeholder,
}) => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  const pageRef = useRef(1);
  const searchRef = useRef('');
  const loadingRef = useRef(false);
  const hasNextRef = useRef(false);
  const requestIdRef = useRef(0);
  const debounceTimerRef = useRef(null);

  const formatItem = useCallback((item) => {
    if (!item) return null;
    const itemCode = item.subTypeNum || item.itemCode || '';
    const rawLabel = item.label || item.name || item.subTypeLabel || '';
    const labelText = itemCode && !rawLabel.includes(itemCode)
      ? `${rawLabel} - ${itemCode}`
      : rawLabel;

    return {
      ...item,
      key: item.id || item.key,
      id: item.id || item.key,
      value: item.id || item.key,
      expenseTypeId: item.expenseTypeId,
      name: rawLabel,
      label: labelText,
      subTypeLabel: rawLabel,
      itemCode: itemCode,
      unitPrice: typeof item.unitPrice !== 'undefined' ? item.unitPrice : 0,
      expenseType: item.expenseType,
      parentId: item.parentId,
    };
  }, []);

  const fetchSubTypes = useCallback(
    async (page, search) => {
      const currentTypeId = typeof expenseTypeId === 'object' ? expenseTypeId?.id : expenseTypeId;
      if (!currentTypeId) {
        requestIdRef.current += 1;
        pageRef.current = 1;
        hasNextRef.current = false;
        loadingRef.current = false;
        setOptions([]);
        setIsLoading(false);
        setHasNext(false);
        return;
      }

      const currentRequestId = requestIdRef.current + 1;
      requestIdRef.current = currentRequestId;
      loadingRef.current = true;
      setIsLoading(true);

      const isStale = () => currentRequestId !== requestIdRef.current;

      try {
        const response = await instance({
          method: 'GET',
          url: `/api/expensetypes/subtype/get?expenseTypeId=${currentTypeId}&vehicleId=${vehicleId || ''}&subTypeNum=&page=${page}&limit=${LIMIT}&search=${encodeURIComponent(search || '')}`,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (isStale()) return;

        const payload = Array.isArray(response)
          ? { data: response }
          : response?.data
          ? response
          : { data: response || [] };

        const rawItems = Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];
        const formattedItems = rawItems.map(formatItem).filter(Boolean);

        let moreAvailable = false;
        if (typeof payload.hasNext === 'boolean') {
          moreAvailable = payload.hasNext;
        } else if (typeof response?.hasNext === 'boolean') {
          moreAvailable = response.hasNext;
        } else if (payload.total || response?.total) {
          const total = payload.total || response?.total;
          moreAvailable = page * LIMIT < total;
        } else {
          moreAvailable = rawItems.length === LIMIT;
        }

        setOptions((prev) => {
          if (page === 1) {
            // Keep selected value in options if editing existing record
            if (value && (value.id || value.key)) {
              const valId = value.id || value.key;
              if (!formattedItems.some((i) => i.id === valId)) {
                const formattedVal = formatItem(value);
                return [formattedVal, ...formattedItems];
              }
            }
            return formattedItems;
          } else {
            // Append new items without duplicates
            const existingIds = new Set(prev.map((i) => i.id));
            const newUnique = formattedItems.filter((i) => !existingIds.has(i.id));
            return [...prev, ...newUnique];
          }
        });

        pageRef.current = page;
        hasNextRef.current = moreAvailable;
        setHasNext(moreAvailable);
      } catch (error) {
        if (isStale() || page !== 1) return;
        try {
          const legacyResponse = await instance({
            method: 'GET',
            url: `/api/expensetypes/subtype?expenseTypeId=${currentTypeId}&search=${encodeURIComponent(search || '')}`,
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          });
          if (isStale()) return;
          const legacyRaw = Array.isArray(legacyResponse)
            ? legacyResponse
            : legacyResponse?.data || [];
          const legacyFormatted = legacyRaw.map(formatItem).filter(Boolean);
          setOptions(legacyFormatted);
          pageRef.current = 1;
          hasNextRef.current = false;
          setHasNext(false);
        } catch (legacyErr) {
          console.error('Error fetching subTypes:', legacyErr);
        }
      } finally {
        if (!isStale()) {
          loadingRef.current = false;
          setIsLoading(false);
        }
      }
    },
    [expenseTypeId, vehicleId, formatItem, value],
  );

  const currentTypeId = typeof expenseTypeId === 'object' ? expenseTypeId?.id : expenseTypeId;

  useEffect(() => {
    pageRef.current = 1;
    searchRef.current = '';
    hasNextRef.current = false;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    fetchSubTypes(1, '');
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [currentTypeId, vehicleId, fetchSubTypes]);

  const handleInputChange = (inputValue, actionMeta) => {
    if (actionMeta && actionMeta.action !== 'input-change') return;
    const search = inputValue || '';
    searchRef.current = search;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      pageRef.current = 1;
      hasNextRef.current = false;
      fetchSubTypes(1, search);
    }, 350);
  };

  const handleMenuScrollToBottom = () => {
    if (!hasNextRef.current || loadingRef.current || !currentTypeId) return;
    const nextPage = pageRef.current + 1;
    fetchSubTypes(nextPage, searchRef.current);
  };

  useEffect(() => {
    if (value && (value.id || value.key)) {
      const valId = value.id || value.key;
      setOptions((prev) => {
        if (!prev.some((item) => item.id === valId)) {
          const formatted = formatItem(value);
          return [formatted, ...prev];
        }
        return prev;
      });
    }
  }, [value, formatItem]);

  const selectedValue =
    value && (value.id || value.key)
      ? {
          key: value.key || value.id,
          id: value.id || value.key,
          value: value.id || value.key,
          label:
            value.label ||
            value.subTypeLabel ||
            value.name ||
            (value.itemCode ? `${value.name || ''} - ${value.itemCode}` : ''),
          name: value.name || value.label || '',
          unitPrice: typeof value.unitPrice !== 'undefined' ? value.unitPrice : 0,
        }
      : '';

  return (
    <SingleSelect
      id={selectName}
      array={options}
      selectName={selectName}
      label={label || (translate ? translate('subType') : 'SubType')}
      placeholder={placeholder}
      value={selectedValue}
      handleChange={handleChange}
      index={index}
      canRemove={canRemove}
      canAssign={canAssign}
      readOnly={readOnly || isDisabled}
      isSearchable={true}
      isLoading={isLoading}
      filterOption={() => true}
      onInputChange={handleInputChange}
      onMenuScrollToBottom={handleMenuScrollToBottom}
      translate={translate}
    />
  );
};

export default SubTypeSelect;
